/**
 * CreditOperationsPage — Tela de Operações de Crédito.
 *
 * 1 linha = 1 operação+aditamento, lida de settlement_v2.vw_operations_list (AF-3400), que
 * junta os dados do domínio credit (tipo, emitente, datas, valores aprovados) com os
 * agregados de title_debt (referência, pago, contagem). O backend já filtra por contrato
 * concluído + faturado.
 *
 * Operação faturada sem título aparece com pago vazio e contagem 0 — sinal de que os eventos
 * de criação de título ainda não chegaram.
 */
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageList from '../../templates/PageList';
import appConfig from '../../../config/app-config';
import { FilterBuilder, serializeFilters } from '../../molecules/FilterBuilder';
import { AppliedFilter } from '../../molecules/FilterBuilder/types';
// Mesmo schema do step 2 do Registrar Recebimento — a listagem de operações filtra os mesmos campos.
import { operationsFilterSchema } from '../operations/constants/operationFilterSchema';
import OperationTitleDebtsModal from './modals/OperationTitleDebtsModal';
import OperationMovementsModal from './modals/OperationMovementsModal';
import MenuButton, { MenuOption } from '../../atoms/MenuButton';
import { formatBRL } from '../../../utils/currencyUtils';
import { FaListOl } from 'react-icons/fa';

const operationTypeLabels: Record<string, string> = {
    LOAN: 'Empréstimo',
    RECEIVABLES_ASSIGNMENT: 'Cessão de recebíveis',
    ACKNOWLEDGEMENT_OF_DEBT: 'Confissão de dívida',
    STRUCTURED_OPERATION: 'Operação estruturada',
};

/** Texto do botão de títulos conforme a natureza do que foi registrado. */
const titlesButtonLabel = (businessReference?: string) => {
    if (businessReference === 'INSTALLMENT') return 'Parcelas';
    if (businessReference === 'RECEIVABLE') return 'Recebíveis';
    return 'Títulos';   // operação faturada cujos títulos ainda não chegaram
};

const MENU_TITLE_DEBTS = 'title-debts';
const MENU_MOVEMENTS = 'movements';

const CreditOperationsPage = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(100);
    const [currentSortField, setCurrentSortField] = useState('sequencial_number');
    const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>([]);
    const [titleDebtsOperation, setTitleDebtsOperation] = useState<any>(null);
    // garantia abre o MESMO modal com natureza própria: ela fica fora dos agregados da operação
    // (pagar uma garantia movimenta o mesmo dinheiro duas vezes), então não pode dividir a lista
    // com as parcelas — a contagem do botão discordaria do que a lista mostra.
    const [guaranteesOperation, setGuaranteesOperation] = useState<any>(null);
    const [movementsOperation, setMovementsOperation] = useState<any>(null);

    const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

    const sortParam = currentSortOrder === 'DESC' ? `-${currentSortField}` : currentSortField;
    const params = useMemo(() => {
        const p = new URLSearchParams({
            page: String(currentPage + 1),
            size: String(currentPageSize),
            sort: sortParam,
        });
        const filterParams = serializeFilters(activeFilters);
        filterParams.forEach((value, key) => p.set(key, value));
        return p;
    }, [currentPage, currentPageSize, sortParam, activeFilters]);

    // '+' não é decodificado pelo API Gateway — espaço tem que ir como %20
    const url = `${API_URL}/operations?${params.toString().replace(/\+/g, '%20')}`;

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setCurrentPage(0);
    }, []);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: API_KEY || '' }
    ], [API_KEY]);

    const tableHeaders = useMemo(() => [
        { id: 'credit_operation_id', title: 'Operação', canSort: false },
        { id: 'contract_label', title: 'Contrato', canSort: true, field: 'contract_label' },
        { id: 'operation_type', title: 'Tipo', canSort: false },
        { id: 'issuer_name', title: 'Emitente / Cedente', canSort: true, field: 'issuer_name' },
        { id: 'approved_value', title: 'Valor aprovado', canSort: true, field: 'approved_value' },
        { id: 'nominal_value', title: 'Valor nominal', canSort: true, field: 'nominal_value' },
        { id: 'paid_amount', title: '$ Recebido', canSort: true, field: 'paid_amount' },
        { id: 'outstanding', title: '$ Em Aberto', canSort: false },
        { id: 'title_debts', title: 'Títulos', canSort: false },
        { id: 'actions', title: 'Ações', canSort: false },
    ], []);

    const menuOptions = useCallback((row: any): MenuOption[] => [
        {
            key: MENU_TITLE_DEBTS,
            label: 'Listagem de Títulos',
            type: 'item',
            backgroundColor: '#2563eb',
            title: 'Abre Cobrança — Títulos filtrado por esta operação',
        },
        {
            key: MENU_MOVEMENTS,
            label: 'Movimentações',
            type: 'item',
            backgroundColor: '#475569',
        },
    ], []);

    const handleMenuSelect = (row: any) => (option: string) => {
        if (option === MENU_MOVEMENTS) return setMovementsOperation(row);
        if (option === MENU_TITLE_DEBTS) {
            // a tela de charges lê estes params da URL e já monta filtrada. Aqui só existe a
            // operação (a linha é uma operação, não um charge), então vai só ela.
            navigate(`/billing/charges?credit_operation_id=${encodeURIComponent(row.credit_operation_id)}`);
        }
    };

    const renderRows = (rows: any[]) => {
        return rows.map((row: any) => {
            const outstanding = (row.nominal_value ?? 0) - (row.paid_amount ?? 0);
            return (
                <tr key={`${row.credit_operation_id}-${row.amendment_number}`} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td className="px-4 py-2 text-sm text-gray-500 font-mono" title={row.credit_operation_id}>
                        {row.credit_operation_id?.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium">{row.contract_label || '-'}</td>
                    <td className="px-4 py-2 text-sm">{operationTypeLabels[row.operation_type] || row.operation_type || '-'}</td>
                    <td className="px-4 py-2 text-sm max-w-[220px] truncate" title={row.issuer_document ? `${row.issuer_name} — ${row.issuer_document}` : row.issuer_name}>
                        {row.issuer_name || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right">{row.approved_value != null ? formatBRL(row.approved_value) : '-'}</td>
                    <td className="px-4 py-2 text-sm text-right">{row.nominal_value != null ? formatBRL(row.nominal_value) : '-'}</td>
                    <td className={`px-4 py-2 text-sm text-right ${row.paid_amount > 0 ? 'text-green-600 font-medium' : ''}`}>
                        {row.paid_amount != null ? formatBRL(row.paid_amount) : '-'}
                    </td>
                    <td className={`px-4 py-2 text-sm text-right ${outstanding > 0 ? 'text-orange-600 font-medium' : ''}`}>
                        {row.nominal_value != null ? formatBRL(outstanding) : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm whitespace-nowrap text-center">
                        <button
                            className="w-[115px] px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 inline-flex items-center justify-center gap-1"
                            onClick={() => setTitleDebtsOperation(row)}
                            title="Ver títulos desta operação"
                        ><FaListOl /> {titlesButtonLabel(row.business_reference)} ({row.title_debts_count ?? 0})</button>
                        {(row.guarantees_count ?? 0) > 0 && (
                            <button
                                className="w-[115px] mt-1 px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 inline-flex items-center justify-center gap-1"
                                onClick={() => setGuaranteesOperation(row)}
                                title="Recebíveis dados em garantia — pagar um deles também amortiza as parcelas"
                            ><FaListOl /> Garantias ({row.guarantees_count})</button>
                        )}
                    </td>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                        <MenuButton
                            options={menuOptions(row)}
                            onSelect={handleMenuSelect(row)}
                            ariaLabel="Ações da operação"
                        />
                    </td>
                </tr>
            );
        });
    };

    return (
        <>
            <PageList
                pageTitle="Operações de Crédito"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Home', url: '/home' },
                    { label: 'Financeiro', url: '#' },
                    { label: 'Operações de Crédito', url: '/settlement/operations' },
                ]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort={currentSortField}
                tableOrderSort={currentSortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => { setCurrentSortField(field); setCurrentSortOrder(order); }}
                renderRows={renderRows}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={(p: number) => setCurrentPage(p)}
                onItemsPerPageChange={(s: number) => { setCurrentPageSize(s); setCurrentPage(0); }}
                itemsLabel="operações"
            >
                {{
                    filterSlot: (
                        <FilterBuilder
                            schema={operationsFilterSchema}
                            onSearch={handleFilterSearch}
                            collapsible
                            defaultCollapsed
                            title="Filtros"
                        />
                    ),
                    controlSlot: (<div></div>),
                }}
            </PageList>
            {guaranteesOperation && (
                <OperationTitleDebtsModal
                    operation={guaranteesOperation}
                    nature="GUARANTEE"
                    onClose={() => setGuaranteesOperation(null)}
                />
            )}
            {titleDebtsOperation && (
                <OperationTitleDebtsModal
                    operation={titleDebtsOperation}
                    onClose={() => setTitleDebtsOperation(null)}
                />
            )}
            {movementsOperation && (
                <OperationMovementsModal
                    operation={movementsOperation}
                    onClose={() => setMovementsOperation(null)}
                />
            )}
        </>
    );
};

export default CreditOperationsPage;
